import {
  arenaCrowdDefaults,
  arenaGrabDefaults,
  arenaRulesDefaults,
  DEFAULT_QUALITY_TIERS,
  lureSitesDefaults,
} from '../../../lib/s3/defaults';
import {
  DENSITY_TIERS,
  FOOTPRINTS,
  LURE_CATEGORIES,
  type ArenaRulesDef,
  type LureSiteRulesDef,
  type VehicleDef,
} from '../../../lib/s3/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import {
  CheckboxField,
  Field,
  Fieldset,
  NumberField,
  NumberInput,
  SelectField,
  StringListEditor,
} from '../fields';
import { useContentBundle } from '../refs';
import type { FormProps } from '../types';
import { TierSelectField } from '../wind/WindForms';
import { IdListEditor, KeyValueEditor } from '../s2/kv';

const opts = (xs: readonly string[]) => xs.map((x) => ({ value: x, label: x }));

/** The arena numbers (`arenaRules`; lurerun.html "The run"). Absent fields show the C# defaults. */
export function ArenaRulesForm({ value, onChange, errors }: FormProps<ArenaRulesDef>) {
  const r = withDefaults(arenaRulesDefaults(), value ?? {}) as Required<ArenaRulesDef>;
  const grab = withDefaults(arenaGrabDefaults(), r.grab ?? {}) as Required<
    NonNullable<ArenaRulesDef['grab']>
  >;
  const crowd = withDefaults(arenaCrowdDefaults(), r.crowd ?? {}) as Required<
    NonNullable<ArenaRulesDef['crowd']>
  >;
  const zombies = (useContentBundle().zombies ?? []).map((z) => z?.id ?? '').filter(Boolean);
  const set = <K extends keyof ArenaRulesDef>(k: K, x: ArenaRulesDef[K]) =>
    onChange({ ...r, [k]: x });
  const f = (
    label: string,
    path: string,
    val: number | undefined,
    put: (n: number) => void,
    o: { integer?: boolean; hint?: string } = {},
  ) => (
    <NumberField
      key={path}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={0}
      value={val}
      issues={errors[path]}
      onChange={(n) => put(n ?? 0)}
    />
  );
  const tiers = r.qualityTiers ?? [];
  const shown = tiers.length ? tiers : [...DEFAULT_QUALITY_TIERS];
  return (
    <>
      <Fieldset legend="The run">
        {f('Time limit s', 'timeLimitS', r.timeLimitS, (n) => set('timeLimitS', n), {
          integer: true,
        })}
        {f('Horde cap', 'hordeCap', r.hordeCap, (n) => set('hordeCap', n), { integer: true })}
        {f('Reinforcements (share of the start)', 'reinforceMaxShare', r.reinforceMaxShare, (n) =>
          set('reinforceMaxShare', n),
        )}
        {f('Brutes at most', 'maxBrutes', r.maxBrutes, (n) => set('maxBrutes', n), {
          integer: true,
        })}
        {f('Pick-up s', 'pickupS', r.pickupS, (n) => set('pickupS', n), {
          hint: 'Stopped in the bay for a survivor.',
        })}
        {f('Fuel can s', 'fuelCanS', r.fuelCanS, (n) => set('fuelCanS', n), {
          hint: 'Tank seconds a street can adds.',
        })}
        {f('Lose track m', 'loseTrackM', r.loseTrackM, (n) => set('loseTrackM', n))}
        {f('Lose track s', 'loseTrackS', r.loseTrackS, (n) => set('loseTrackS', n))}
      </Fieldset>
      <Fieldset legend="The server's roll and the validator's slack">
        {f(
          'Start tank share',
          'startTankShare',
          r.startTankShare,
          (n) => set('startTankShare', n),
          {
            hint: "The lender's tank at the start (0.7 of 200 s → 140 s).",
          },
        )}
        {f('Runner share', 'runnerShare', r.runnerShare, (n) => set('runnerShare', n), {
          hint: 'Of a rolled horde (150 → 25).',
        })}
        {f('Brutes per 100', 'brutesPer100', r.brutesPer100, (n) => set('brutesPer100', n))}
        {f('Min-run slack', 'minRunSlack', r.minRunSlack, (n) => set('minRunSlack', n), {
          hint: 'The shortest run x this bounds the duration and fuel (above 0, at most 1).',
        })}
      </Fieldset>
      <Fieldset legend="Device tiers (horde caps)">
        <Field
          label="High, mid, low"
          hint={
            tiers.length
              ? 'Clear all three to fall back to 300/220/150.'
              : 'Empty: TierCaps() uses 300/220/150 (shown).'
          }
          issues={errors.qualityTiers}
        >
          {() => (
            <div className="grid grid-cols-3 gap-1">
              {[0, 1, 2].map((i) => (
                <NumberInput
                  key={i}
                  label={`Quality tier ${['high', 'mid', 'low'][i]}`}
                  integer
                  min={0}
                  value={shown[i]}
                  onChange={(n) => {
                    const next = [...shown];
                    next[i] = n ?? 0;
                    set('qualityTiers', next);
                  }}
                />
              ))}
            </div>
          )}
        </Field>
        <CheckboxField
          label="Use the code defaults (empty list)"
          checked={tiers.length === 0}
          onChange={(on) => set('qualityTiers', on ? [] : [...DEFAULT_QUALITY_TIERS])}
        />
      </Fieldset>
      <Fieldset legend="Grab">
        {f('Radius m', 'grab.radiusM', grab.radiusM, (n) => set('grab', { ...grab, radiusM: n }))}
        {f('Stop below m/s', 'grab.stopMps', grab.stopMps, (n) =>
          set('grab', { ...grab, stopMps: n }),
        )}
        {f('Base s', 'grab.baseS', grab.baseS, (n) => set('grab', { ...grab, baseS: n }))}
        {f('Per kg s', 'grab.perKgS', grab.perKgS, (n) => set('grab', { ...grab, perKgS: n }), {
          hint: `A 120 kg pickup load takes ${Math.round(((grab.baseS ?? 0) + (grab.perKgS ?? 0) * 120) * 10) / 10} s.`,
        })}
      </Fieldset>
      <Fieldset legend="Clingers">
        <KeyValueEditor<number>
          label="Claw damage per second"
          base="clingDps"
          kind="number"
          keys={zombies}
          hint="By zombie id (shambler 4, runner 3, brute 10)."
          value={r.clingDps}
          errors={errors}
          onChange={(x) => set('clingDps', x)}
        />
      </Fieldset>
      <Fieldset legend="Crowd tier">
        {f(
          'Hero clingers',
          'crowd.heroClingers',
          crowd.heroClingers,
          (n) => set('crowd', { ...crowd, heroClingers: n }),
          { integer: true },
        )}
        {f('Sim Hz', 'crowd.simHz', crowd.simHz, (n) => set('crowd', { ...crowd, simHz: n }), {
          integer: true,
        })}
        {f(
          'Far sim Hz',
          'crowd.farSimHz',
          crowd.farSimHz,
          (n) => set('crowd', { ...crowd, farSimHz: n }),
          { integer: true },
        )}
        {f(
          'Flow field Hz',
          'crowd.flowHz',
          crowd.flowHz,
          (n) => set('crowd', { ...crowd, flowHz: n }),
          { integer: true },
        )}
        {f('Hull within m', 'crowd.hullWithinM', crowd.hullWithinM, (n) =>
          set('crowd', { ...crowd, hullWithinM: n }),
        )}
      </Fieldset>
    </>
  );
}

/** Lure-run destinations (`lureSites`; lurerun.html "On the map", M7). Absent fields show the C# defaults; off by default. */
export function LureSitesForm({ value, onChange, errors }: FormProps<LureSiteRulesDef>) {
  const r = withDefaults(lureSitesDefaults(), value ?? {}) as Required<LureSiteRulesDef>;
  const b = useContentBundle();
  const vehicles = ((b.vehicles ?? []) as (VehicleDef | null)[])
    .map((v) => v?.id ?? '')
    .filter(Boolean);
  const tables = (b.lootTables ?? []).map((t) => t?.id ?? '').filter(Boolean);
  const set = <K extends keyof LureSiteRulesDef>(k: K, x: LureSiteRulesDef[K]) =>
    onChange({ ...r, [k]: x });
  const f = (
    label: string,
    path: keyof LureSiteRulesDef,
    o: { integer?: boolean; hint?: string } = {},
  ) => (
    <NumberField
      key={path}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={0}
      value={r[path] as number}
      issues={errors[path]}
      onChange={(n) => set(path, (n ?? 0) as never)}
    />
  );
  return (
    <>
      <Fieldset legend="On the map">
        <CheckboxField
          label="Enabled"
          hint="Off: no lure-run destinations and no Overrun trucks, without an app release."
          checked={!!r.enabled}
          onChange={(on) => set('enabled', on)}
        />
        {f('Chance per set', 'chancePerSet', { hint: 'Per set of 12 destinations (0..1).' })}
        {f('Most per set', 'maxPerSet', {
          integer: true,
          hint: 'Inside the cap of 4 specials (M7).',
        })}
        {f('Window h', 'windowH', { hint: 'Open from when it first shows.' })}
        {f('Min distance m', 'minDistanceM')}
        {f('Stranded share', 'strandedShare', {
          hint: 'Survivor pick-ups instead of a supply truck (0..1).',
        })}
        {f('Overrun share', 'overrunShare', {
          hint: 'Supply-truck world events that spawn as a lure run (0..1).',
        })}
        <IdListEditor
          label="Categories"
          base="categories"
          what="category"
          options={opts(LURE_CATEGORIES)}
          value={r.categories}
          errors={errors}
          onChange={(x) => set('categories', x)}
        />
        <SelectField
          label="Min footprint"
          value={r.minFootprint}
          options={opts(FOOTPRINTS)}
          issues={errors.minFootprint}
          onChange={(x) => set('minFootprint', x ?? '')}
        />
      </Fieldset>
      <Fieldset legend="Horde and vehicles">
        <KeyValueEditor<number>
          label="Horde by density tier"
          base="hordeByTier"
          kind="number"
          keys={DENSITY_TIERS}
          value={r.hordeByTier}
          errors={errors}
          onChange={(x) => set('hordeByTier', x)}
        />
        <IdListEditor
          label="Start unlocked"
          base="startUnlocked"
          what="vehicle"
          hint="M6: the e-bike and the motorcycle."
          options={opts(vehicles)}
          value={r.startUnlocked}
          errors={errors}
          onChange={(x) => set('startUnlocked', x)}
        />
        <KeyValueEditor<number>
          label="Lent vehicle weights"
          base="vehicleWeights"
          kind="number"
          keys={vehicles}
          hint="Where no mission fixes the vehicle (an Overrun truck)."
          value={r.vehicleWeights}
          errors={errors}
          onChange={(x) => set('vehicleWeights', x)}
        />
      </Fieldset>
      <Fieldset legend="Cargo">
        <SelectField
          label="Cargo loot table"
          value={r.cargoLootTableId}
          options={opts(tables)}
          none="(none)"
          issues={errors.cargoLootTableId}
          onChange={(x) => set('cargoLootTableId', x ?? '')}
        />
        {f('Cargo rolls', 'cargoRolls', { integer: true })}
      </Fieldset>
      <Fieldset legend="Stranded survivor">
        <TierSelectField
          label="Stranded tier (WIND)"
          hint="The stranded survivor's wind tier at pick-up."
          value={r.strandedTier ?? 'winded'}
          issues={errors.strandedTier}
          onChange={(x) => set('strandedTier', x)}
        />
        {f('Health share at pick-up (old)', 'strandedHealthShare', {
          hint: 'Old, kept one content version: the stranded tier replaces it. Above 0, at most 1.',
        })}
        <StringListEditor
          label="Name pool"
          base="strandedNames"
          placeholder="Marisol|she"
          hint='"Name|pronouns" entries, as in the rescue name pools. Empty = "Stranger".'
          value={r.strandedNames}
          errors={errors}
          onChange={(x) => set('strandedNames', x)}
        />
      </Fieldset>
    </>
  );
}
